/**
 * Context-scoped store for the Keycloak BFF auth state.
 *
 * `AuthProvider` wraps the whole app, so the auth state lives in a per-mount store
 * (one instance, held via a React context) rather than `useState`. Two reasons: the
 * latest access token is read imperatively with `getState()` — no ref shadow — and
 * `useAuthInfo` subscribes via `useSyncExternalStore`. This is a tiny hand-rolled
 * store (the same vanilla-store + useSyncExternalStore pattern Zustand wraps), kept
 * dependency-free so the package carries NO runtime dep that consuming apps must
 * teach their bundler about.
 */
import { createContext } from 'react'

import type {
  UseAuthInfoNotLoggedInProps,
  UseAuthInfoProps,
} from '@sweetauth/core/types'

import { buildLoggedInState, type SessionResponse } from './session'

interface AuthState {
  /** The three-state discriminated union that `useAuthInfo` returns. */
  authInfo: UseAuthInfoProps
  /** Re-fetch `/auth/refresh` and update `authInfo`. */
  refresh: () => Promise<void>
  setSessionError: (message: string) => void
}

/** Minimal vanilla-store surface consumed by `useAuthInfo` and the provider. */
export interface AuthStore {
  getState: () => AuthState
  getInitialState: () => AuthState
  subscribe: (listener: () => void) => () => void
}

function notLoggedIn(
  refresh: () => Promise<void>,
  sessionError: string,
): UseAuthInfoNotLoggedInProps {
  return {
    loading: false,
    isLoggedIn: false,
    sessionError,
    accessToken: null,
    user: null,
    userClass: null,
    orgHelper: null,
    accessHelper: null,
    isImpersonating: false,
    impersonatorUserId: undefined,
    accessTokenExpiresAtSeconds: undefined,
    refreshAuthInfo: refresh,
    tokens: {
      getAccessToken: async () => undefined,
      getAccessTokenForOrg: async () => ({
        error: 'unexpected_error' as const,
        accessToken: undefined as never,
      }),
    },
  }
}

function loadingState(
  refresh: () => Promise<void>,
  getAccessToken: () => Promise<string | undefined>,
): UseAuthInfoProps {
  return {
    loading: true,
    isLoggedIn: undefined,
    accessToken: undefined,
    user: undefined,
    userClass: undefined,
    orgHelper: undefined,
    accessHelper: undefined,
    isImpersonating: undefined,
    impersonatorUserId: undefined,
    accessTokenExpiresAtSeconds: undefined,
    refreshAuthInfo: refresh,
    tokens: {
      getAccessToken,
      getAccessTokenForOrg: async () => ({
        error: 'unexpected_error' as const,
        accessToken: undefined as never,
      }),
    },
  }
}

/** Outcome of a BFF session refresh — shared by the store's refresh and the fetch-wrapper's retry. */
type RefreshOutcome =
  | { status: 'ok'; session: SessionResponse }
  | { status: 'unauthenticated' }
  | { status: 'error'; message: string }

const serviceUnavailableMessage =
  'The sign-in service is unavailable. Try again in a moment.'

// A single in-flight refresh at a time, shared across every caller: the store's own mount/activity
// refresh AND the global fetch wrapper's 401 retry. The BFF rotates the one-time-use refresh token,
// so two concurrent refreshes would race and one would 401 (→ a spurious sign-out); single-flighting
// collapses them into one POST whose result everyone awaits.
let inFlightRefresh: Promise<RefreshOutcome> | null = null

async function doRefresh(authUrl: string): Promise<RefreshOutcome> {
  try {
    const res = await fetch(`${authUrl}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
    if (res.status === 401) return { status: 'unauthenticated' }
    if (!res.ok) {
      return {
        status: 'error',
        message:
          res.status >= 500
            ? serviceUnavailableMessage
            : "We couldn't finish signing you in. Try again, or contact your administrator if this continues.",
      }
    }
    const session: SessionResponse = await res.json()
    return { status: 'ok', session }
  } catch {
    return { status: 'error', message: serviceUnavailableMessage }
  }
}

/** Refresh the BFF session, single-flighted — concurrent callers all await the one POST. */
export function refreshSession(authUrl: string): Promise<RefreshOutcome> {
  if (!inFlightRefresh) {
    inFlightRefresh = doRefresh(authUrl).finally(() => {
      inFlightRefresh = null
    })
  }
  return inFlightRefresh
}

/**
 * Boolean wrapper for the global fetch wrapper's 401 handler: refresh once (single-flighted with the
 * store's own refresh) and report whether the session is still alive, so the wrapper can replay the
 * request. False (dead session or network error) → the wrapper lets the 401 through.
 */
export async function refreshSessionForFetch(
  authUrl: string,
): Promise<boolean> {
  return (await refreshSession(authUrl)).status === 'ok'
}

export function createAuthStore(
  authUrl: string,
  onUnauthenticated?: () => void,
): AuthStore {
  const listeners = new Set<() => void>()
  let state: AuthState

  const getState = (): AuthState => state
  const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }
  const setAuthInfo = (authInfo: UseAuthInfoProps): void => {
    state = { ...state, authInfo }
    listeners.forEach((listener) => listener())
  }

  // Reads the current token from the store at call time, so callers always get the
  // most-recently-refreshed value without holding a stale closure.
  const getAccessToken = async (): Promise<string | undefined> =>
    state.authInfo.accessToken ?? undefined

  const refresh = async (): Promise<void> => {
    if (typeof window === 'undefined') return
    const outcome = await refreshSession(authUrl)
    if (outcome.status === 'ok') {
      setAuthInfo(buildLoggedInState(outcome.session, refresh, getAccessToken))
      return
    }
    if (outcome.status === 'unauthenticated') {
      // If the app WAS authenticated, the session just went away mid-flight (our idle/absolute-cap
      // sign-out, or a revoke) and the app has a handler — hand off to it (it shows the signed-out
      // dialog) instead of hard-redirecting out from under that dialog. A cold "never signed in"
      // load (not previously logged in, or no handler) starts the Keycloak PKCE flow via server_v2,
      // preserving the current path so the BFF callback returns the user here after login.
      if (state.authInfo.isLoggedIn === true && onUnauthenticated) {
        onUnauthenticated()
        return
      }
      const returnTo =
        window.location.origin +
        window.location.pathname +
        window.location.search
      window.location.href = `${authUrl}/auth/login?redirect=${encodeURIComponent(returnTo)}`
      return
    }
    // Offline / CORS / unexpected server error — fall back to not-logged-in (no redirect loop).
    setSessionError(outcome.message)
  }

  const setSessionError = (message: string): void => {
    setAuthInfo(notLoggedIn(refresh, message))
  }

  const initialState: AuthState = {
    authInfo: loadingState(refresh, getAccessToken),
    refresh,
    setSessionError,
  }
  state = initialState
  return { getState, getInitialState: () => initialState, subscribe }
}

export const AuthStoreContext = createContext<AuthStore | null>(null)
